import React, { useMemo, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  countriesByCountryCodeSelector,
  isLoadingScenariosMembersDataSelector,
  scenarioMembersDataForMemberListSelector,
} from './store/selectors';
import {
  fetchCountries,
  fetchScenarioMembersWithData,
  fetchScenarioMembersWithComparingData,
} from './store/actions';

import ScenarioMemberListGrid from './scenarioMemberListGrid.component';
import getColumnDefinitions from './columnDefinitions';
import ScenarioNavbar from '../shared/scenarioNavbar.component';
import MultipleCurrenciesWarning from '../shared/multipleCurrenciesWarning.component';
import {
  selectedScenarioCurrencySelector,
  selectedScenarioToCompareSelector,
  isLoadingCalculatingScenarioSelector,
  reportsNamesSelector,
  isLoadingReportFieldsSelector,
  isUploadingTaggedDataSelector, scenariosSelector,
} from '../shared/store/selectors';
import { uniqByFields } from '../shared/utils';
import {
  fetchScenarioReportFields,
} from '../shared/store/actions';
import { ScenarioMemberDataPropType, ScenarioPropType } from '../shared/propTypes';
import { environmentSuffixSelector } from '../../shared/store/selectors';
import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../userData/store/selectors';
import useGridApi from '../../shared/hooks/useGridApi.hook';
import downloadScenarioMembersXlsx from './downloadScenarioMembersXlsx';
import { uniq } from '../../shared/uniq';
import { useRestoreScenarioViewOptions } from '../shared/useRestoreScenarioViewOptions.hook';

const useCollectDataFromGroupedRows = ({ gridApi }) => useCallback(() => {
  const entities = [];
  const topGroupColumn = gridApi.getRowGroupColumns()[0];

  const traverseGroupsTree = (node, parentsGroups) => {
    if (node.aggData) {
      const group = [...parentsGroups, node.aggData[node.field]];
      const groupDescription = `-> ${group.join(', ')}`;
      entities.push({
        ...node.aggData,
        groupDescription,
      });
      node.childrenAfterGroup.map(childNode => traverseGroupsTree(childNode, group));
    } else {
      entities.push(node.data);
    }
  };

  if (topGroupColumn) {
    gridApi.forEachNode(node => {
      if (node.aggData && node.field === topGroupColumn.colDef.field) {
        traverseGroupsTree(node, []);
      }
    });
  } else {
    gridApi.forEachNode(node => {
      entities.push(node);
    });
  }

  return {
    entities,
  };
}, [gridApi]);

const ScenarioMemberListPage = ({
  match: {
    params: {
      scenarioId,
    },
  },
  selectedScenarioToCompare,
  selectedCurrency,

  fetchScenarioMembersWithData,
  fetchScenarioMembersWithComparingData,
  isLoadingScenarioMembersData,
  scenarioMembersData,

  fetchScenarioReportFields,
  isLoadingReportFields,
  reportsNames,


  countries,
  fetchCountries,

  scenarios,

  isLoadingCalculating,
  isUploadingTaggedData,

  environmentSuffix,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
}) => {
  useRestoreScenarioViewOptions(scenarioId);
  useEffect(() => {
    fetchCountries();
    fetchScenarioReportFields();
  }, [fetchCountries, fetchScenarioReportFields]);

  const fetchData = useCallback(() => {
    if (!reportsNames.length) {
      return;
    }

    if (selectedScenarioToCompare) {
      fetchScenarioMembersWithComparingData({
        scenarioId,
        compareScenarioId: selectedScenarioToCompare,
        reportingCurrency: selectedCurrency,
        reportsNames,
      });
    } else if (scenarioId) {
      fetchScenarioMembersWithData({
        scenarioId,
        reportingCurrency: selectedCurrency,
        reportsNames,
      });
    }
  }, [
    scenarioId,
    selectedScenarioToCompare,
    selectedCurrency,
    fetchScenarioMembersWithData,
    fetchScenarioMembersWithComparingData,
    reportsNames,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hasDifferentCurrencies = useMemo(() =>
    scenarioMembersData && uniq(scenarioMembersData.map(({ FUNCTIONAL_CURRENCY }) => FUNCTIONAL_CURRENCY)).length > 1,
  [scenarioMembersData]
  );

  const baseScenarioMetadata = useMemo(
    () => scenarios.find(scenario => scenario.scenarioId === Number(scenarioId)),
    [scenarios, scenarioId]
  );

  const compareScenarioMetadata = useMemo(
    () => scenarios.find(scenario => scenario.scenarioId === Number(selectedScenarioToCompare)),
    [scenarios, selectedScenarioToCompare]
  );

  const uniqueCalcData = useMemo(
    () => uniqByFields(scenarioMembersData, ['COMPANY_CODE', 'COUNTRY_NAME', 'COUNTRY_TAX_TYPE']),
    [scenarioMembersData]
  );

  const companyCodeContainingCountryName = uniqueCalcData.reduce((acc, data) => {
    if (data.COUNTRY_NAME) {
      acc[data.COMPANY_CODE] = true;
    }
    return acc;
  }, {});

  // display only data that has country name or does not contain country name at all
  const displayedCalcData = uniqueCalcData
    .filter(data => data.COUNTRY_NAME || !companyCodeContainingCountryName[data.COMPANY_CODE]);

  const columnDefinitions = useMemo(() => getColumnDefinitions({
    compareScenarioId: selectedScenarioToCompare,
    scenarioId,
    hasDifferentCurrencies,
  }), [selectedScenarioToCompare, scenarioId, hasDifferentCurrencies]);

  const isLoading = isLoadingReportFields
    || isUploadingTaggedData
    || isLoadingScenarioMembersData
    || isLoadingCalculating;

  const { gridApi, onGridReady } = useGridApi();
  const collectDataFromGrid = useCollectDataFromGroupedRows({ gridApi });

  const exportToExcel = useCallback(done => {
    downloadScenarioMembersXlsx({
      columnDefinitions,
      ...collectDataFromGrid(),
      baseScenario: baseScenarioMetadata,
      compareScenario: compareScenarioMetadata,
      shouldDisplayZero,
      environmentSuffix,
      hideDecimals,
      negativeMinus,
    }).finally(done);
  }, [
    collectDataFromGrid,
    columnDefinitions,
    baseScenarioMetadata,
    compareScenarioMetadata,
    shouldDisplayZero,
    environmentSuffix,
    hideDecimals,
    negativeMinus,
  ]);

  return (
    <div className="scenario-member-list">
      <div className="page">
        <div className="container-fluid">
          <ScenarioNavbar
            onRefresh={fetchData}
            exportToExcel={exportToExcel}
            disableExporting={isLoading}
          />
          <div>
            <MultipleCurrenciesWarning hasDifferentCurrencies={hasDifferentCurrencies} />

            <ScenarioMemberListGrid
              scenarioId={Number(scenarioId)}
              compareScenarioId={selectedScenarioToCompare}
              isLoading={isLoading}
              calcData={displayedCalcData}
              countries={countries}
              columnDefinitions={columnDefinitions}
              onGridReady={onGridReady}
              comparingMode={Boolean(selectedScenarioToCompare)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};


ScenarioMemberListPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      scenarioId: PropTypes.string.isRequired,
    }),
  }),

  fetchCountries: PropTypes.func.isRequired,
  countries: PropTypes.objectOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    currencyCode: PropTypes.string.isRequired,
  })),
  scenarios: PropTypes.arrayOf(ScenarioPropType),

  selectedScenarioToCompare: PropTypes.number,
  selectedCurrency: PropTypes.string,

  fetchScenarioReportFields: PropTypes.func.isRequired,
  isLoadingReportFields: PropTypes.bool.isRequired,
  reportsNames: PropTypes.arrayOf(PropTypes.string),

  fetchScenarioMembersWithData: PropTypes.func.isRequired,
  fetchScenarioMembersWithComparingData: PropTypes.func.isRequired,

  isLoadingScenarioMembersData: PropTypes.bool,
  scenarioMembersData: PropTypes.arrayOf(ScenarioMemberDataPropType),

  isLoadingCalculating: PropTypes.bool.isRequired,
  isUploadingTaggedData: PropTypes.bool.isRequired,
  environmentSuffix: PropTypes.string,
  shouldDisplayZero: PropTypes.bool,
  hideDecimals: PropTypes.bool,
  negativeMinus: PropTypes.bool,
};

const mapStateToProps = state => ({
  countries: countriesByCountryCodeSelector(state),
  scenarios: scenariosSelector(state),

  selectedScenarioToCompare: selectedScenarioToCompareSelector(state),
  selectedCurrency: selectedScenarioCurrencySelector(state),

  reportsNames: reportsNamesSelector(state),
  isLoadingReportFields: isLoadingReportFieldsSelector(state),

  isLoadingScenarioMembersData: isLoadingScenariosMembersDataSelector(state),
  scenarioMembersData: scenarioMembersDataForMemberListSelector(state),

  isLoadingCalculating: isLoadingCalculatingScenarioSelector(state),
  isUploadingTaggedData: isUploadingTaggedDataSelector(state),

  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),
});

export default connect(
  mapStateToProps,
  {
    fetchCountries,
    fetchScenarioReportFields,
    fetchScenarioMembersWithData,
    fetchScenarioMembersWithComparingData,
  }
)(ScenarioMemberListPage);


